import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { ensureTrailerExists } from "@/lib/trailer-utils";
import { syncTrailerBol } from "@/lib/trailer-bol-sync";
import {
  TrailerCrossTenantDialog,
  findTrailerInOtherTenants,
  type OtherTenantTrailerMatch,
} from "@/components/trailers/TrailerCrossTenantDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMemo } from "react";

interface InlineEditCellProps {
  orderId: string;
  fieldName: string;
  currentValue: string | null;
  placeholder?: string;
  /** Required when fieldName is "trailer_id" to auto-create trailers */
  orderTrailerTypeId?: string | null;
  /** Required when fieldName is "trailer_id" to set trailer location */
  orderShipFromCity?: string | null;
  /** When true, empty state renders an em dash instead of "+ Add". */
  readOnly?: boolean;
}

export function InlineEditCell({
  orderId,
  fieldName,
  currentValue,
  placeholder = "Enter value",
  orderTrailerTypeId,
  orderShipFromCity,
  readOnly = false,
}: InlineEditCellProps) {
  const [value, setValue] = useState(currentValue || "");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { tenantId, activeTenant } = useTenant();

  const isEmpty = !currentValue || currentValue.trim() === "";

  // Cross-tenant detection state for trailer_id assignment
  const [crossTenantMatches, setCrossTenantMatches] = useState<OtherTenantTrailerMatch[]>([]);
  const [showCrossTenantDialog, setShowCrossTenantDialog] = useState(false);
  const [pendingTrailerValue, setPendingTrailerValue] = useState<string | null>(null);
  // When the user explicitly opts to create a separate record, bypass the
  // cross-tenant detection on the immediate retry to avoid an infinite loop.
  const [skipCrossTenantOnce, setSkipCrossTenantOnce] = useState(false);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = async (rawValue?: string) => {
    const nextValue = (rawValue ?? value).trim();
    if (!nextValue || nextValue === (currentValue ?? "")) {
      setIsEditing(false);
      setValue(currentValue || "");
      return;
    }

    setIsSaving(true);
    try {
      // Cross-tenant detection: if assigning a trailer_id, check whether the
      // trailer already exists in another tenant before we auto-provision a
      // new local record. If hits, open the migration dialog and defer the
      // actual save until the user resolves it.
      if (fieldName === "trailer_id" && tenantId && !skipCrossTenantOnce) {
        const { data: existsHere } = await supabase
          .from("trailers")
          .select("id")
          .eq("trailer_id", nextValue)
          .eq("tenant_id", tenantId)
          .maybeSingle();
        if (!existsHere) {
          const matches = await findTrailerInOtherTenants(nextValue, tenantId);
          if (matches.length > 0) {
            setPendingTrailerValue(nextValue);
            setCrossTenantMatches(matches);
            setShowCrossTenantDialog(true);
            setIsSaving(false);
            return;
          }
        }
      }
      if (skipCrossTenantOnce) setSkipCrossTenantOnce(false);

      // Update the order. Do NOT auto-confirm here — confirmation now requires
      // both a tractor and a trailer and runs through the edit dialog / mutation
      // gating in useOrderMutations. Inline edits stay as pure local field writes.
      const updatePayload: Record<string, string> = { [fieldName]: nextValue };
      const { error } = await supabase
        .from("orders")
        .update(updatePayload)
        .eq("id", orderId);

      if (error) throw error;

      // Auto-create trailer if assigning a trailer_id
      if (fieldName === "trailer_id") {
        await ensureTrailerExists({
          trailerId: nextValue,
          trailerTypeId: orderTrailerTypeId,
          shipFromCity: orderShipFromCity,
          tenantId,
        });
      }

      // Log the change
      await supabase.from("order_change_log").insert({
        order_id: orderId,
        field_name: fieldName,
        old_value: currentValue || null,
        new_value: nextValue,
        changed_by: user?.email || "Unknown",
      });

      queryClient.invalidateQueries({ queryKey: ["orders"] });
      if (fieldName === "trailer_id") {
        queryClient.invalidateQueries({ queryKey: ["trailers"] });
        // Best-effort: push any pre-existing trailer BoL to S3 tagged with
        // this order's unique_id. Runs after the local write succeeds.
        void syncTrailerBol({
          direction: "order-gains-trailer",
          orderId,
          tenantSlug: activeTenant,
        }).then((r) => {
          if (r.synced > 0) {
            queryClient.invalidateQueries({ queryKey: ["order-detail", orderId] });
            queryClient.invalidateQueries({ queryKey: ["bol-count", orderId] });
            queryClient.invalidateQueries({ queryKey: ["bol-images", orderId] });
            queryClient.invalidateQueries({ queryKey: ["bol-images-count", orderId] });
            queryClient.invalidateQueries({ queryKey: ["bol-extractions", orderId] });
          }
        });
      }
      toast.success(`${fieldName.replace(/_/g, " ")} updated`);
      setIsEditing(false);
      setValue(nextValue);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save");
      setValue(currentValue || "");
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setValue(currentValue || "");
    }
  };

  // If has value, show as read-only text
  if (!isEmpty) {
    return (
      <span className="text-sm">{currentValue}</span>
    );
  }

  if (readOnly) {
    return <span className="text-sm">—</span>;
  }

  // For trailer_id we keep the same "+ Add" affordance as Tractor/Operator,
  // but the click opens a popover hosting the same checked-in trailer
  // combobox the create/edit dialogs use (tenant-scoped, type-ahead, with a
  // "Use ..." escape hatch for new ids).
  // NOTE: this branch must come BEFORE the generic `isEditing` Input branch,
  // because trailer_id reuses `isEditing` as the popover's open state.
  if (fieldName === "trailer_id") {
    return (
      <>
        <InlineTrailerIdMenu
          open={isEditing}
          setOpen={setIsEditing}
          isSaving={isSaving}
          onSelect={(v) => {
            setValue(v);
            void handleSave(v);
          }}
        />
        {pendingTrailerValue && (
          <TrailerCrossTenantDialog
            open={showCrossTenantDialog}
            onOpenChange={(o) => {
              setShowCrossTenantDialog(o);
              if (!o) {
                setPendingTrailerValue(null);
                setCrossTenantMatches([]);
              }
            }}
            trailerIdText={pendingTrailerValue}
            matches={crossTenantMatches}
            onMigrated={() => {
              const v = pendingTrailerValue;
              setPendingTrailerValue(null);
              setCrossTenantMatches([]);
              if (v) void handleSave(v);
            }}
            onCreateSeparate={() => {
              const v = pendingTrailerValue;
              setPendingTrailerValue(null);
              setCrossTenantMatches([]);
              setSkipCrossTenantOnce(true);
              if (v) void handleSave(v);
            }}
          />
        )}
      </>
    );
  }

  // If empty and editing (non-trailer fields)
  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => handleSave()}
        disabled={isSaving}
        className="h-7 text-sm w-24"
        placeholder={placeholder}
      />
    );
  }

  // If empty and not editing, show clickable placeholder
  return (
    <button
      onClick={() => setIsEditing(true)}
      className="text-sm text-muted-foreground hover:text-foreground hover:underline cursor-pointer"
    >
      + Add
    </button>
  );
}

interface InlineTrailerIdMenuProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  isSaving: boolean;
  onSelect: (trailerId: string) => void;
}

/**
 * Inline "+ Add" affordance for the Trailer ID column that opens the same
 * type-ahead menu used for Tractor/Operator (see InlineAssignCell). The menu
 * is sourced from checked-in trailers for the active tenant, with a "Use ..."
 * escape hatch that lets the user commit a new trailer id.
 */
function InlineTrailerIdMenu({
  open,
  setOpen,
  isSaving,
  onSelect,
}: InlineTrailerIdMenuProps) {
  const { tenantId } = useTenant();
  const [search, setSearch] = useState("");

  const { data: trailers = [], isLoading } = useQuery({
    queryKey: ["inline-checked-in-trailers", tenantId],
    enabled: open && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailers")
        .select(
          "id, trailer_id, load_status, current_location:locations!trailers_current_location_id_fkey(short_code)",
        )
        .eq("tenant_id", tenantId!)
        .eq("current_location_status", "at_site")
        .order("trailer_id");
      if (error) throw error;
      return (data ?? []) as unknown as Array<{
        id: string;
        trailer_id: string;
        load_status: string | null;
        current_location: { short_code: string } | null;
      }>;
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return trailers;
    return trailers.filter((t) => t.trailer_id.toLowerCase().includes(q));
  }, [trailers, search]);

  const trimmed = search.trim();
  const hasExact = trailers.some(
    (t) => t.trailer_id.toLowerCase() === trimmed.toLowerCase(),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-sm text-muted-foreground hover:text-foreground hover:underline cursor-pointer disabled:opacity-50"
          disabled={isSaving}
        >
          + Add
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[260px] p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search checked-in trailers..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[260px] overflow-y-auto">
            {filtered.length === 0 && !trimmed && (
              <CommandEmpty>
                {isLoading ? "Loading…" : "No checked-in trailers found."}
              </CommandEmpty>
            )}
            {filtered.length > 0 && (
              <CommandGroup heading={`Checked-in trailers (${trailers.length})`}>
                {filtered.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={t.trailer_id}
                    disabled={isSaving}
                    onSelect={() => onSelect(t.trailer_id)}
                  >
                    <Check className={cn("mr-2 h-4 w-4 opacity-0")} />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-medium">{t.trailer_id}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {[
                          t.current_location?.short_code,
                          t.load_status && t.load_status !== "unknown"
                            ? t.load_status.charAt(0).toUpperCase() +
                              t.load_status.slice(1).toLowerCase()
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {trimmed && !hasExact && (
              <CommandGroup>
                <CommandItem
                  value={`__use__${trimmed}`}
                  disabled={isSaving}
                  onSelect={() => onSelect(trimmed)}
                >
                  <span className="text-sm">
                    Use <span className="font-medium">"{trimmed}"</span>
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
